import Typography from '@material-ui/core/Typography';
import AdditionalDetail from './AdditionalDetail';
import LabelledSwitch from '../core/LabelledSwitch';
import Notification from '../core/Notification';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { ENTITLEMENTS, EDITING_OPTIONS } from '../application-core/constants';
import ActionText from '../core/ActionText';
import BasicLanguageFieldDisplay from '../core/BasicLanguageFieldDisplay';
import Errored from '../core/Errored';
import { ProjectConsumer } from '../core/providers/ProjectProvider';
import { globalStyles as styles } from '../core/style/gstheme';
import TitledPanel from '../core/TitledPanel';
import DatatypeDisplay from '../core/DatatypeDisplay';
import { MAX_DESCRIPTION_LENGTH, STATUS_ACCEPTED, STATUS_REJECTED } from '../core/utils/constants';
import { checkResourceEntitlements, getArrayValues, getGraphFromProject, isInContextComponent, makeCancelable } from '../core/utils/utils';
import Warning from '../core/Warning';
import withGS from '../core/withGShoc';
import GS from '../grafsync/grafsync';
import { MAX_CLASSNAME_LENGTH } from './constants';
import ResourceDropRelationshipsDisplay from './ResourceDropRelationshipsDisplay';
import ResourceRelationshipsDisplay from './ResourceRelationshipsDisplay';
import { MAX_DEFINITION_LENGTH, EXPANDED_NOTE_FIELDS } from '../vocabulary/constants';
import { getPairwiseValues } from '../vocabulary/utils/utils';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import DocumentSource from './DocumentSource';
import Visibility from './Visibility';
import AddButton from '../core/AddButton';
import WorkflowResourceManager from '../core/workflow/WorkflowResourceManager';

const properties = [
    { rel: 'subPropertyOf', title: "Is Subproperty Of", allowExternal: true, searchClass: "http://www.w3.org/2002/07/owl#DatatypeProperty" },
    { rel: 'hasSubProperty', title: "Subproperties", searchClass: "http://www.w3.org/2002/07/owl#DatatypeProperty" },
    { rel: 'propertyDisjointWith', title: "Disjoint With", allowExternal: true, searchClass: "http://www.w3.org/2002/07/owl#DatatypeProperty" },
    { rel: 'equivalentProperty', title: "Equivalent Property", allowExternal: true, searchClass: "http://www.w3.org/2002/07/owl#DatatypeProperty" }
];

const classProperties = [
    {
        rel: 'domain',
        title: "Domain",
        restrictionProperty: true,
        expressions: true,
        searchClass: "http://www.w3.org/2002/07/owl#Class"
    }
];

class DatatypePropertyDetail extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            warning: "",
            isErrored: null
        };
        this.cancellable = null;
    }

    addNewLiteral = (property, propertyGraphIRI) => (language, text) => {
        GS.addProperty(this.props.id, this.props.graph, property, text, language, null, propertyGraphIRI);
    };

    addNewTypedLiteral = (property, propertyGraphIRI) => (value, datatype = null) => {
        GS.addProperty(this.props.id, this.props.graph, property, value, null, datatype, propertyGraphIRI);
    };

    addNewResource = (property, propertyGraphIRI, suppliedPropertyType) => (iri) => {
        GS.loadData([iri], {}, this.props.graph).then(() => GS.addProperty(this.props.id, this.props.graph, property, iri, null, null, propertyGraphIRI, suppliedPropertyType));
    };

    addDefinedBy = () => GS.addProperty(this.props.id, this.props.graph, "isDefinedBy", this.props.ontologyIRI);

    onUpdate = (property, lang, index, propertyGraphIRI) => (value) => this.props.onUpdate(property, value, lang, index, propertyGraphIRI);

    onDelete = (property, lang, datatype, propertyGraphIRI) => (value) => this.props.onDelete(property, value, lang, datatype, propertyGraphIRI);

    toggleClass = (alias) => () => {
        if (this.props.data.type.includes(alias)) {
            this.onDeleteClass(alias);
        }
        else {
            this.onAddClass(alias);
        }
    };

    onAddClass = (value) => this.props.onAdd("type", value);

    onDeleteClass = (value) => this.props.onDelete("type", value);

    onExitWarning = () => this.setState({ warning: "" });

    doPath(payload) {
        this.cancellable = makeCancelable(GS.path(payload));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    checkProperty = async (id, property) => {
        const { data } = this.props;
        if (this.props.id === id) return "You cannot add relations where a property is related to itself.";
        if (data.propertyDisjointWith && data.propertyDisjointWith.includes(id)) {
            return "You cannot add a property that is already disjoint.";
        }
        if (data.equivalentProperty && data.equivalentProperty.includes(id)) {
            return "You cannot add a property that is already an equivalent property.";
        }
        if (data.subPropertyOf && data.subPropertyOf.includes(id)) {
            return "You cannot add a property that is already a subproperty of.";
        }
        if (data.hasSubProperty && data.hasSubProperty.includes(id)) {
            return "You cannot add a property that is already a subproperty.";
        }
        const subPropertyOf = await this.doPath({
            graph: getGraphFromProject(this.props.project),
            source: this.props.id,
            path: "http://www.w3.org/2000/01/rdf-schema#subPropertyOf"
        }).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return null;
        if (subPropertyOf.results) {
            if (subPropertyOf.results.some(result => result.parent === id)) {
                return "You cannot add a property that is already above in the property hierarchy.";
            }
        }
        const hasSubProperty = await this.doPath({
            graph: getGraphFromProject(this.props.project),
            source: this.props.id,
            path: "https://graphifi.com/schema/xowl/hasSubProperty"
        }).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return null;
        if (hasSubProperty.results) {
            if (hasSubProperty.results.some(result => result.parent === id)) {
                return "You cannot add a property that is already lower in the property hierarchy.";
            }
        }
        return null;
    };

    moveProperty = (property) => (id) => {
        if (this.props.data[property] && this.props.data[property].includes(id)) return;
        this.checkProperty(id, property)
            .then(result => {
                if (result === null) this.addNewResource(property)(id);
                else {
                    if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ warning: result });
                }
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
    };

    render() {

        const { data, classes, defaultLang, project, ontologyIRI, buildLink, graph, viewLang, excludeDeprecated,
            hiddenProps, id, availableAnnotationProperty, editingOptions, projectUsers, useWorkflow, entitlements } = this.props;
        const { warning, isErrored } = this.state;

        if (data._notAvailable) return null;
        if (warning !== "") return <Warning message={warning} onExitWarning={this.onExitWarning} />;
        if (isErrored) return <Errored data={isErrored} />;
        if (data.pendingReplacement) return <ActionText>This property has been replaced. Select again to view new data.</ActionText>;

        const isReadOnly = checkResourceEntitlements(data, ENTITLEMENTS.EDIT_ONTOLOGY)
            || (data.deprecated === true)
            || (useWorkflow && "status" in data && (data.status[0] === STATUS_ACCEPTED || data.status[0] === STATUS_REJECTED));

        const hidden = hiddenProps ?
            hiddenProps.map(prop => prop.substring(36))
            :
            [];
        const visibleNoteFields = EXPANDED_NOTE_FIELDS.filter(field => !hidden.includes(field.property));
        const av = editingOptions && editingOptions.includes(EDITING_OPTIONS.ADVANCED_VIEW);
        const dl = editingOptions && editingOptions.includes(EDITING_OPTIONS.OWL_DL);
        const isDefinedBy = getArrayValues(data, "isDefinedBy");

        return (
            <div>
                <ProjectConsumer>
                    {({ id: projectIRI, projectLang, hasComponent }) => (
                        <form autoComplete="off" className={classes.selection}>
                            {data.pendingDeletion &&
                                <Notification error={true} text="This property is pending deletion." dataTest="pending-deletion" />
                            }
                            {data.deprecated &&
                                <div>
                                    <Notification text="This property is deprecated." dataTest="deprecated" />
                                    {
                                        excludeDeprecated && data.subPropertyOf && data.subPropertyOf.includes("http://www.w3.org/2002/07/owl#topDataProperty") &&
                                        <Notification text="This property is hidden at the top level of the hierarchy." dataTest="deprecated-hidden-note" />
                                    }
                                </div>
                            }
                            <Grid container spacing={1}>
                                {useWorkflow &&
                                    <Grid item xs={12}>
                                        <WorkflowResourceManager
                                            disabled={!isInContextComponent(data, ontologyIRI) || data.deprecated || data.pendingDeletion}
                                            status={data.status}
                                            id={id}
                                            key={id}
                                            resourceClass="http://www.w3.org/2002/07/owl#DatatypeProperty"
                                            componentIRI={ontologyIRI}
                                            entitlements={entitlements}
                                            defaultLang={defaultLang}
                                            assignedUser={data.assignedUser}
                                            assignedNote={data.assignedNote}
                                            rejectedNote={data.rejectedNote}
                                            projectUsers={projectUsers}
                                            project={project}
                                            graph={graph} />
                                    </Grid>
                                }
                                <Grid item md={12} lg={6}>
                                    <BasicLanguageFieldDisplay
                                        dataTest="resource-label"
                                        message={"Add a Label"}
                                        defaultLang={viewLang || defaultLang}
                                        title="Label"
                                        property="label"
                                        values={data.label}
                                        projectLang={projectLang}
                                        placeholder="Enter label text"
                                        name="Label"
                                        maxLength={MAX_CLASSNAME_LENGTH}
                                        onePerLang={true}
                                        graph={graph}
                                        onDelete={this.onDelete}
                                        onUpdate={this.onUpdate}
                                        isReadOnly={isReadOnly}
                                        onAddLanguage={this.addNewLiteral} />
                                </Grid>
                                <Grid item md={12} lg={6}>
                                    <TitledPanel dataTest="isDefinedBy" height="full" title="Is Defined By"
                                        actions={!isReadOnly && isDefinedBy.length === 0 ?
                                            <AddButton onClick={this.addDefinedBy} dataTest="add-isdefined-by" title="Make defined by this ontology" />
                                            :
                                            null
                                        }>
                                        {isDefinedBy.map(idb =>
                                            <CommonIriDisplay
                                                id={idb}
                                                key={idb}
                                                components={hasComponent}
                                                projectIRI={project}
                                                handleNotAvailable={true}
                                                property="isDefinedBy"
                                                isReadOnly={true}
                                                compact={true}
                                                graph={graph}
                                                source={id}
                                                wrap={false}
                                                viewLang={viewLang}
                                                defaultLang={defaultLang} />
                                        )}
                                    </TitledPanel>
                                </Grid>
                                {
                                    properties.map((item, index) => {
                                        const itemData = data[item.rel] ?
                                            (Array.isArray(data[item.rel]) ? data[item.rel] : [data[item.rel]])
                                            : [];
                                        const filteredItemData = itemData.filter(item => item !== "http://www.w3.org/2002/07/owl#topDataProperty");
                                        const ebp = [];
                                        ebp.push({
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#equivalentProperty"
                                            ],
                                            target: data.id
                                        });
                                        ebp.push({
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#propertyDisjointWith"
                                            ],
                                            target: data.id
                                        });
                                        ebp.push({
                                            predicate: [
                                                "http://www.w3.org/2000/01/rdf-schema#subPropertyOf"
                                            ],
                                            target: data.id,
                                            reverse: true
                                        });
                                        ebp.push({
                                            predicate: [
                                                "https://graphifi.com/schema/xowl/hasSubProperty"
                                            ],
                                            target: data.id,
                                            reverse: true
                                        });
                                        if (item.rel === "subPropertyOf") {
                                            ebp.push({
                                                predicate: [
                                                    "http://www.w3.org/2000/01/rdf-schema#subPropertyOf"
                                                ],
                                                target: data.id,
                                                pathOperators: ["+"]
                                            });
                                        }
                                        if (item.rel === "hasSubProperty") {
                                            ebp.push({
                                                predicate: [
                                                    "https://graphifi.com/schema/xowl/hasSubProperty"
                                                ],
                                                target: data.id,
                                                pathOperators: ["+"]
                                            });
                                        }
                                        let exclude = [];
                                        if (data.equivalentProperty) exclude = exclude.concat(data.equivalentProperty);
                                        if (data.propertyDisjointWith) exclude = exclude.concat(data.propertyDisjointWith);
                                        if (data.subPropertyOf) exclude = exclude.concat(data.subPropertyOf);
                                        if (data.hasSubProperty) exclude = exclude.concat(data.hasSubProperty);
                                        exclude = [...new Set(exclude)];
                                        return <Grid item md={12} lg={6} key={"rel-" + index}>
                                            <ResourceDropRelationshipsDisplay
                                                dataTest={item.rel}
                                                project={project}
                                                onMove={this.moveProperty(item.rel)}
                                                addNewResource={this.addNewResource(item.rel)}
                                                property={item.rel}
                                                graph={graph}
                                                title={item.title}
                                                excludeByPath={ebp}
                                                exclude={exclude}
                                                useWorkflow={useWorkflow}
                                                components={hasComponent}
                                                defaultLang={defaultLang}
                                                view="datatypeProperty"
                                                permitExternal={item.allowExternal}
                                                permittedExternalType={item.allowExternal ? "DatatypeProperty" : null}
                                                buildLink={buildLink}
                                                searchClass={item.searchClass}
                                                source={data.id}
                                                viewLang={viewLang}
                                                ontologyIRI={ontologyIRI}
                                                values={filteredItemData}
                                                isReadOnly={isReadOnly} />
                                        </Grid>;
                                    })
                                }
                                {
                                    classProperties.map((item, index) => {
                                        const itemData = data[item.rel] ?
                                            (Array.isArray(data[item.rel]) ?
                                                data[item.rel]
                                                :
                                                [data[item.rel]])
                                            :
                                            [];
                                        const ebp = [];
                                        const spValues = GS.getSuperPropertyValues(data.id, graph, item.rel);
                                        spValues.forEach(item => {
                                            // Existing item disjoint with result
                                            ebp.push({
                                                predicate: [
                                                    "http://www.w3.org/2002/07/owl#disjointWith",
                                                    "https://graphifi.com/schema/xowl/hasSubClass"
                                                ],
                                                target: item,
                                                reverse: true,
                                                pathOperators: ["", "*"]
                                            });
                                            // Result disjoint with existing item
                                            ebp.push({
                                                predicate: [
                                                    "http://www.w3.org/2000/01/rdf-schema#subClassOf",
                                                    "http://www.w3.org/2002/07/owl#disjointWith"
                                                ],
                                                target: item,
                                                pathOperators: ["*", ""]
                                            });
                                        });
                                        return <Grid item md={12} lg={6} key={"rel-" + index}>
                                            <ResourceRelationshipsDisplay
                                                dataTest={item.rel}
                                                project={project}
                                                addNewResource={this.addNewResource(item.rel)}
                                                property={item.rel}
                                                includeSystemGraph={true}
                                                title={item.title}
                                                excludeByPath={ebp}
                                                useWorkflow={useWorkflow}
                                                restrictionProperty={av && item.restrictionProperty}
                                                components={hasComponent}
                                                expressions={av && item.expressions}
                                                permitExternal={item.allowExternal}
                                                view="class"
                                                buildLink={buildLink}
                                                graph={graph}
                                                searchClass={item.searchClass}
                                                source={data.id}
                                                defaultLang={defaultLang}
                                                viewLang={viewLang}
                                                ontologyIRI={ontologyIRI}
                                                values={itemData}
                                                isReadOnly={isReadOnly} />
                                        </Grid>;
                                    })
                                }
                                <Grid item md={12} lg={6}>
                                    <ResourceRelationshipsDisplay
                                        dataTest="range"
                                        project={project}
                                        addNewResource={this.addNewResource("range")}
                                        property="range"
                                        includeSystemGraph={true}
                                        title="Range"
                                        useWorkflow={useWorkflow}
                                        restrictionProperty={false}
                                        components={hasComponent}
                                        expressions={av && (!data.range || data.range.length === 0)}
                                        target="Literal"
                                        maxValues={1}
                                        permitExternal={false}
                                        view="datatypeProperty"
                                        buildLink={buildLink}
                                        graph={graph}
                                        searchClass="http://www.w3.org/2002/07/owl#DatatypeProperty"
                                        source={data.id}
                                        defaultLang={defaultLang}
                                        viewLang={viewLang}
                                        ontologyIRI={ontologyIRI}
                                        values={data.range}
                                        isReadOnly={isReadOnly} />
                                </Grid>
                                {!hiddenProps.includes("http://www.w3.org/2000/01/rdf-schema#comment") &&
                                    <Grid item md={12} lg={6}>
                                        <BasicLanguageFieldDisplay
                                            dataTest="comments"
                                            message={"Add a comment"}
                                            defaultLang={viewLang || defaultLang}
                                            placeholder="Enter comment text"
                                            title="Comment"
                                            property="comment"
                                            maxLength={MAX_DESCRIPTION_LENGTH}
                                            graph={graph}
                                            values={data.comment}
                                            projectLang={projectLang}
                                            multiline={true}
                                            onDelete={this.onDelete}
                                            onUpdate={this.onUpdate}
                                            isReadOnly={isReadOnly}
                                            onAddLanguage={this.addNewLiteral} />
                                    </Grid>
                                }
                                {!hiddenProps.includes("http://www.w3.org/2000/01/rdf-schema#seeAlso") &&
                                    [
                                        {
                                            rel: 'seeAlso',
                                            title: "See Also"
                                        }
                                    ].map((item, index) => {
                                        const itemData = data[item.rel] ?
                                            (Array.isArray(data[item.rel]) ? data[item.rel] : [data[item.rel]])
                                            : [];
                                        return <Grid item md={12} lg={6} key={"rel-" + index}>
                                            <DatatypeDisplay
                                                title={item.title}
                                                source={data.id}
                                                datatype={""}
                                                defaultLang={defaultLang}
                                                viewLang={viewLang}
                                                useWorkflow={useWorkflow}
                                                projectLang={projectLang}
                                                property={item.rel}
                                                values={itemData}
                                                onDelete={this.onDelete}
                                                context={ontologyIRI}
                                                onUpdate={this.onUpdate}
                                                projectIRI={projectIRI}
                                                noRangeOptions={{
                                                    language: false,
                                                    dateTime: false,
                                                    datatype: true,
                                                    boolean: false,
                                                    excludeDatatypes: ["INTEGER", "NNINTEGER", "DECIMAL", "LONG", "FLOAT", "DATE", "DOUBLE"]
                                                }}
                                                graph={graph}
                                                associatedProjectGraph={graph}
                                                isReadOnly={isReadOnly}
                                                canAddValues={true}
                                                addNewResource={this.addNewResource(item.rel, graph, "@id")}
                                                onAddLiteral={this.addNewLiteral(item.rel)}
                                                onAddTypedLiteral={this.addNewTypedLiteral(item.rel)}
                                            />
                                        </Grid>;
                                    })
                                }
                                <Grid item md={12} lg={6}>
                                    <TitledPanel height="full" title="Characteristics">
                                        <LabelledSwitch
                                            dataTest="functional"
                                            disabled={isReadOnly}
                                            isReadOnly={isReadOnly}
                                            label="Functional"
                                            checked={data.type.includes("FunctionalProperty")}
                                            handleChange={this.toggleClass("FunctionalProperty")}
                                        />
                                        {data.type.includes("InverseFunctionalProperty") &&
                                            <React.Fragment>
                                                {dl &&
                                                    <Notification text="Datatype properties should not be inverse functional." dataTest="ifp-datatype" />
                                                }
                                                <LabelledSwitch
                                                    dataTest="inversefunctional"
                                                    disabled={isReadOnly}
                                                    isReadOnly={isReadOnly}
                                                    label="Inverse Functional"
                                                    checked={data.type.includes("InverseFunctionalProperty")}
                                                    handleChange={this.toggleClass("InverseFunctionalProperty")}
                                                />
                                            </React.Fragment>
                                        }
                                    </TitledPanel>
                                </Grid>
                                <Grid item md={12} lg={6}>
                                    <Visibility
                                        graph={graph}
                                        viewLang={viewLang}
                                        componentIRI={ontologyIRI}
                                        source={id}
                                        sourceType={data.type}
                                        defaultLang={defaultLang}
                                        sources={data.visibility} />
                                </Grid>
                                <Grid item md={12} lg={6}>
                                    <DocumentSource
                                        dataTest="document-source-list"
                                        graph={graph}
                                        componentIRI={ontologyIRI}
                                        viewLang={viewLang}
                                        defaultLang={defaultLang}
                                        sources={data.documentSource} />
                                </Grid>
                            </Grid>
                            {visibleNoteFields.length > 0 &&
                                <React.Fragment>
                                    <Typography
                                        data-test="annotation-props-title"
                                        className={classes.subMainHeading}
                                        style={{ marginTop: '20px' }}>SKOS Annotations</Typography>
                                    <Grid data-test="additional-annotations" container spacing={1} classes={
                                        {
                                            "spacing-xs-2": this.props.classes.fullGrid
                                        }
                                    }>
                                        {EXPANDED_NOTE_FIELDS.filter(field => !hidden.includes(field.property)).map(field => {
                                            let existingValues = undefined;
                                            if (field.pairwise) {
                                                existingValues = getPairwiseValues(data);
                                            }
                                            return <Grid key={field.title} item md={12} lg={6}>
                                                <BasicLanguageFieldDisplay
                                                    dataTest={field.property}
                                                    message={"Add " + field.message}
                                                    defaultLang={viewLang || defaultLang}
                                                    title={field.title}
                                                    required={false}
                                                    onePerLang={field.onePerLang === true}
                                                    property={field.property}
                                                    values={data[field.property]}
                                                    existingValues={existingValues}
                                                    maxLength={field.maxLength || MAX_DEFINITION_LENGTH}
                                                    projectLang={projectLang}
                                                    placeholder={field.placeholder}
                                                    name={field.message}
                                                    onDelete={this.onDelete}
                                                    onUpdate={this.onUpdate}
                                                    isReadOnly={isReadOnly}
                                                    multiline={field.multiline}
                                                    onAddLanguage={this.addNewLiteral} />
                                            </Grid>;
                                        }
                                        )}
                                    </Grid>
                                </React.Fragment>
                            }
                            {availableAnnotationProperty && availableAnnotationProperty.length > 0 &&
                                <React.Fragment>
                                    <Typography
                                        data-test="annotation-props-title"
                                        className={classes.subMainHeading}
                                        style={{ marginTop: '20px' }}>Additional Annotations</Typography>
                                    <Grid data-test="additional-annotations" container spacing={1} classes={
                                        {
                                            "spacing-xs-2": this.props.classes.fullGrid
                                        }
                                    }>
                                        <AdditionalDetail
                                            data={data}
                                            projectIRI={project}
                                            viewLang={viewLang}
                                            defaultLang={defaultLang}
                                            projectLang={projectLang}
                                            graph={graph}
                                            useWorkflow={useWorkflow}
                                            ontologyIRI={ontologyIRI}
                                            isReadOnly={isReadOnly}
                                            onDelete={this.onDelete}
                                            onUpdate={this.onUpdate}
                                            context={ontologyIRI}
                                            addNewTypedLiteral={this.addNewTypedLiteral}
                                            addNewLiteral={this.addNewLiteral}
                                            addNewResource={this.addNewResource}
                                            availableAnnotationProperty={availableAnnotationProperty} />
                                    </Grid>
                                </React.Fragment>
                            }
                        </form>
                    )}
                </ProjectConsumer>
                <div data-test="info-panel-bottom" >&#160;</div>
            </div>
        );
    }
}

DatatypePropertyDetail.defaultProps = {
    hiddenProps: [],
    useWorkflow: false
};

DatatypePropertyDetail.propTypes = {
    classes: PropTypes.object,
    id: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    defaultLang: PropTypes.string.isRequired,
    excludeDeprecated: PropTypes.bool,
    buildLink: PropTypes.func.isRequired,
    project: PropTypes.string.isRequired,
    viewLang: PropTypes.string,
    projectUsers: PropTypes.array,
    useWorkflow: PropTypes.bool,
    entitlements: PropTypes.array.isRequired,
    hiddenProps: PropTypes.array,
    ontologyIRI: PropTypes.string.isRequired,
    availableAnnotationProperty: PropTypes.array,
    onAdd: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired
};

export default withStyles(styles)(withGS(DatatypePropertyDetail, ["ALL"], null, {}, true));